import AuditReportsPackages from "./evidence/AuditReportsPackages";
import OperationalCaptureAction from "./operational-capture/OperationalCaptureAction";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../dashboard.css";
import EvidenceIntelligencePanel from "./evidence/EvidenceIntelligencePanel";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import { Button } from "../design-system/components";
import { useAdvisor } from "../AdvisorContext";

const REQUEST_TIMEOUT_MS = 30000;

const EVIDENCE_EXPLAIN_PROMPTS = [
  "Can I trust this evidence?",
  "What does this prove?",
  "Is anything wrong with it?",
  "How does this fit into the audit trail?",
];

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function includesNormalized(haystack, needle) {
  const h = normalizeText(haystack);
  const n = normalizeText(needle);
  if (!n) return false;
  return h.includes(n);
}

function cleanValue(value, fallback = "—") {
  const text = String(value ?? "").trim();
  if (!text) return fallback;

  const lowered = text.toLowerCase();
  if (lowered === "undefined" || lowered === "null") return fallback;

  return text;
}


function formatTimestamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatRelativeTime(value) {
  if (!value) return "—";

  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return "—";

  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function MetricCard({
  label,
  value,
  footnote,
  tone = "default",
  onClick,
  active = false,
}) {
  const toneStyle =
    tone === "danger"
      ? { color: "var(--danger)" }
      : tone === "warn"
      ? { color: "var(--warn)" }
      : tone === "ok"
      ? { color: "var(--ok)" }
      : {};

  const interactive = typeof onClick === "function";

  const content = (
    <div
      className="hero-kpi"
      style={{
        cursor: interactive ? "pointer" : "default",
        transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
        borderColor: active ? "var(--ring)" : undefined,
        boxShadow: active
          ? "0 0 0 2px color-mix(in srgb, var(--ring), transparent 62%)"
          : undefined,
      }}
    >
      <div className="kpi-label">{label}</div>
      <div className="kpi-value small" style={toneStyle}>
        {value}
      </div>
      {footnote ? <div className="hero-kpi-foot">{footnote}</div> : null}
    </div>
  );

  if (!interactive) return content;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ all: "unset", display: "block" }}
      title={`Jump to ${label}`}
    >
      {content}
    </button>
  );
}

function MetaField({ label, value, wrap = false }) {
  const safe = cleanValue(value);

  return (
    <div className="open-item-pill" style={{ cursor: "default", alignItems: "flex-start" }}>
      <div className="open-item-label">{label}</div>
      <div
        className="open-item-value"
        style={{
          fontSize: 12,
          maxWidth: "100%",
          textAlign: "right",
          whiteSpace: wrap ? "normal" : "nowrap",
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "clip" : "ellipsis",
          wordBreak: wrap ? "break-word" : "normal",
          lineHeight: 1.35,
        }}
        title={safe}
      >
        {safe}
      </div>
    </div>
  );
}

function ProofBadge({ label, tone = "default", strong = false }) {
  const style =
    tone === "ok"
      ? {
          background: "color-mix(in srgb, var(--ok), transparent 88%)",
          color: "var(--ok)",
        }
      : tone === "warn"
      ? {
          background: "color-mix(in srgb, var(--warn), transparent 88%)",
          color: "var(--warn)",
        }
      : tone === "danger"
      ? {
          background: "color-mix(in srgb, var(--danger), transparent 88%)",
          color: "var(--danger)",
        }
      : {
          background: "color-mix(in srgb, var(--text), transparent 90%)",
          color: "var(--muted)",
        };

  return (
    <span
      style={{
        ...style,
        borderRadius: 999,
        padding: strong ? "5px 10px" : "4px 9px",
        fontSize: 11,
        fontWeight: strong ? 800 : 700,
        border: strong ? "1px solid color-mix(in srgb, currentColor, transparent 72%)" : "none",
      }}
    >
      {label}
    </span>
  );
}

function FilterChip({ label, active, onClick, tone = "default" }) {
  const activeStyle =
    tone === "ok"
      ? {
          borderColor: "color-mix(in srgb, var(--ok), transparent 35%)",
          background: "color-mix(in srgb, var(--ok), transparent 90%)",
          color: "var(--ok)",
        }
      : tone === "warn"
      ? {
          borderColor: "color-mix(in srgb, var(--warn), transparent 35%)",
          background: "color-mix(in srgb, var(--warn), transparent 90%)",
          color: "var(--warn)",
        }
      : tone === "danger"
      ? {
          borderColor: "color-mix(in srgb, var(--danger), transparent 35%)",
          background: "color-mix(in srgb, var(--danger), transparent 90%)",
          color: "var(--danger)",
        }
      : {
          borderColor: "var(--ring)",
          background: "color-mix(in srgb, var(--ring), transparent 90%)",
          color: "var(--text)",
        };

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "1px solid",
        borderColor: active
          ? activeStyle.borderColor
          : "color-mix(in srgb, var(--text), transparent 84%)",
        background: active ? activeStyle.background : "transparent",
        color: active ? activeStyle.color : "var(--muted)",
        borderRadius: 999,
        padding: "7px 11px",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        transition: "all 120ms ease",
      }}
    >
      {label}
    </button>
  );
}

function normalizeList(payload, preferredKeys = []) {
  for (const key of preferredKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function deriveArchiveStatus(item) {
  const valid = item?.verification_valid;
  const status = normalizeText(item?.verification_status || item?.status);

  if (valid === true || status === "valid" || status === "verified") {
    return { label: "Verified", tone: "ok" };
  }
  if (valid === false || status === "invalid" || status === "failed") {
    return { label: "Invalid", tone: "danger" };
  }
  if (status === "pending" || status === "queued") {
    return { label: "Pending", tone: "warn" };
  }
  return {
    label: cleanValue(item?.verification_status || item?.status, "Unknown"),
    tone: "default",
  };
}

function deriveSelectedArchiveStatus(selectedArchive, archiveDetail, archiveVerification) {
  if (archiveVerification) return deriveArchiveStatus(archiveVerification);
  if (archiveDetail) return deriveArchiveStatus(archiveDetail);
  return deriveArchiveStatus(selectedArchive);
}

function deriveArchiveSurfaceStatus(item, isSelected, selectedStatus) {
  if (isSelected) return selectedStatus;

  const explicitStatus = normalizeText(item?.verification_status || item?.status);
  const explicitValid = item?.verification_valid;

  if (explicitValid === false || explicitStatus === "invalid" || explicitStatus === "failed") {
    return { label: "Invalid", tone: "danger" };
  }

  if (explicitStatus === "pending" || explicitStatus === "queued") {
    return { label: "Pending", tone: "warn" };
  }

  if (explicitStatus === "missing_file") {
    return { label: "Missing", tone: "warn" };
  }

  return { label: "Recorded", tone: "default" };
}


function buildContextSourceLabel(context) {
  const source = normalizeText(context?.sourcePage);
  if (source === "drift") return "Scoped from Drift";
  if (source === "alerts" || source === "alert") return "Scoped from Alert";
  if (source === "posture") return "Scoped from Posture";
  return "Scoped Context";
}

function normalizeRouteValue(value) {
  return cleanValue(value, "");
}

function hasScopedContext(context) {
  return Boolean(
    normalizeRouteValue(context?.framework) ||
      normalizeRouteValue(context?.controlId) ||
      normalizeRouteValue(context?.ruleId) ||
      normalizeRouteValue(context?.resourceId) ||
      normalizeRouteValue(context?.evidenceRef) ||
      normalizeRouteValue(context?.archiveId) ||
      normalizeRouteValue(context?.intervalId)
  );
}

function archiveMatchesContext(item, context) {
  if (!item || !context) return false;

  const framework = normalizeRouteValue(context.framework);
  const controlId = normalizeRouteValue(context.controlId);
  const ruleId = normalizeRouteValue(context.ruleId);
  const resourceId = normalizeRouteValue(context.resourceId);
  const evidenceRef = normalizeRouteValue(context.evidenceRef);
  const archiveId = normalizeRouteValue(context.archiveId);

  const frameworkMatch = !framework || normalizeText(item.framework) === normalizeText(framework);
  const controlMatch =
    !controlId ||
    normalizeText(item.control_id) === normalizeText(controlId) ||
    includesNormalized(item.control_id, controlId) ||
    includesNormalized(item.rule_id, controlId);
  const ruleMatch =
    !ruleId ||
    normalizeText(item.rule_id) === normalizeText(ruleId) ||
    includesNormalized(item.rule_id, ruleId) ||
    includesNormalized(item.control_id, ruleId);
  const resourceMatch =
    !resourceId ||
    includesNormalized(item.resource_id, resourceId) ||
    includesNormalized(item.source_ref, resourceId) ||
    includesNormalized(item.evidence_ref, resourceId);
  const evidenceMatch =
    !evidenceRef ||
    normalizeText(item.evidence_ref) === normalizeText(evidenceRef) ||
    includesNormalized(item.evidence_ref, evidenceRef);
  const archiveMatch =
    !archiveId ||
    normalizeText(item.id) === normalizeText(archiveId) ||
    normalizeText(item.archive_id) === normalizeText(archiveId);

  return (
    hasScopedContext(context) &&
    frameworkMatch &&
    controlMatch &&
    ruleMatch &&
    resourceMatch &&
    evidenceMatch &&
    archiveMatch
  );
}

function reportMatchesContext(item, context) {
  if (!item || !context) return false;
  const framework = normalizeRouteValue(context.framework);
  const evidenceRef = normalizeRouteValue(context.evidenceRef);

  const frameworkMatch = !framework || normalizeText(item.framework) === normalizeText(framework);
  const evidenceMatch =
    !evidenceRef ||
    includesNormalized(item.evidence_ref, evidenceRef) ||
    includesNormalized(item.sha, evidenceRef);

  return Boolean(framework || evidenceRef) && frameworkMatch && evidenceMatch;
}

function submissionMatchesContext(item, context) {
  if (!item || !context) return false;
  const framework = normalizeRouteValue(context.framework);
  const evidenceRef = normalizeRouteValue(context.evidenceRef);

  const frameworkMatch = !framework || normalizeText(item.framework) === normalizeText(framework);
  const evidenceMatch =
    !evidenceRef ||
    includesNormalized(item.evidence_ref, evidenceRef) ||
    includesNormalized(item.linked_pack_id, evidenceRef);

  return Boolean(framework || evidenceRef) && frameworkMatch && evidenceMatch;
}

function joinIncludedSections(value) {
  if (Array.isArray(value)) return value.join(", ");
  return cleanValue(value);
}

function scrollToSection(ref) {
  if (!ref?.current) return;
  ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
}

function getReportPackHref(report, apiBaseUrl = "") {
  if (!report) return null;

  const rawBase = String(apiBaseUrl || "").trim().replace(/\/$/, "");
  const directCandidates = [
    report.download_url,
    report.pack_url,
    report.url,
    report.href,
    report.open_url,
  ];

  for (const candidate of directCandidates) {
    const value = String(candidate || "").trim();
    if (!value) continue;

    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }

    if (value.startsWith("/")) {
      if (!rawBase) return value;

      const baseHasV1 = /\/v1$/i.test(rawBase);
      const pathHasV1 = /^\/v1(\/|$)/i.test(value);

      if (baseHasV1 && pathHasV1) {
        return `${rawBase.replace(/\/v1$/i, "")}${value}`;
      }

      return `${rawBase}${value}`;
    }
  }

  return null;
}

function isS3BackedArchive(item) {
  return Boolean(item?.s3_bucket && item?.s3_key);
}

function isMissingArchive(item) {
  return normalizeText(item?.verification_status) === "missing_file" || item?.file_exists === false;
}

function archivePriorityScore(item, selectedStatus) {
  const effectiveStatus = selectedStatus || deriveArchiveStatus(item);
  const verified = effectiveStatus?.label === "Verified";
  const s3 = isS3BackedArchive(item);
  const missing = isMissingArchive(item);
  const createdAt = new Date(item?.created_at || item?.updated_at || 0).getTime() || 0;

  if (verified && s3) return 5000000000000 + createdAt;
  if (s3 && !missing) return 4000000000000 + createdAt;
  if (verified) return 3000000000000 + createdAt;
  if (!missing) return 1000000000000 + createdAt;
  return createdAt;
}

function buildArchiveSourceBadges(item) {
  const badges = [];

  if (isS3BackedArchive(item)) {
    badges.push({ label: "S3-backed", tone: "ok" });
  } else {
    badges.push({ label: "Legacy", tone: "warn" });
  }

  if (isMissingArchive(item)) {
    badges.push({ label: "Missing Artifact", tone: "warn" });
  } else {
    badges.push({ label: "Artifact Present", tone: "ok" });
  }

  return badges;
}

function buildSelectedArchiveNarrative(selectedArchive, archiveDetail, archiveVerification) {
  const s3Backed =
    Boolean(archiveVerification?.resolved_bucket && archiveVerification?.resolved_key) ||
    Boolean(archiveDetail?.s3_bucket && archiveDetail?.s3_key) ||
    Boolean(selectedArchive?.s3_bucket && selectedArchive?.s3_key);

  const missing =
    normalizeText(archiveVerification?.reason) === "file_missing" ||
    isMissingArchive(archiveDetail || selectedArchive);

  const verified = archiveVerification?.verification_valid === true;
  const invalid = archiveVerification?.verification_valid === false;

  if (verified && s3Backed) {
    return {
      title: "Trusted current proof",
      body:
        "This record is verified and backed by S3 proof storage. Integrity, chain continuity, and timestamp validation are all passing for the currently selected evidence.",
      tone: "ok",
    };
  }

  if (invalid && missing) {
    return {
      title: "Legacy record with missing artifact",
      body:
        "This historical record was created under an earlier storage model. The underlying artifact is not currently available for verification, so evidentiary trust cannot be fully established from this record alone.",
      tone: "warn",
    };
  }

  if (invalid) {
    return {
      title: "Integrity verification failed",
      body:
        "The record exists, but the deeper integrity verification failed. From an audit standpoint, this weakens evidentiary trust until the mismatch is resolved.",
      tone: "danger",
    };
  }

  if (s3Backed) {
    return {
      title: "Current proof record",
      body:
        "This record is linked to current proof storage and is suitable for deeper verification and export workflows.",
      tone: "ok",
    };
  }

  return {
    title: "Historical evidence record",
    body:
      "This record appears to come from an earlier storage model. It may still be useful for context, but current proof records should be preferred for audit-grade verification.",
    tone: "default",
  };
}



function inferDownloadFilename(href, fallback = "report-pack.zip") {
  const raw = String(href || "").trim();
  if (!raw) return fallback;
  try {
    const url = new URL(raw, window.location.origin);
    const pathname = url.pathname || "";
    const last = pathname.split("/").filter(Boolean).pop() || "";
    if (last && last !== "download" && last !== "open") return last;

    const prior = pathname.split("/").filter(Boolean).slice(-2, -1)[0] || "";
    if (prior) return `${prior}.zip`;
  } catch (_) {
    return fallback;
  }
  return fallback;
}

function getLinkedArchiveId(value) {
  return (
    value?.archive_id ??
    value?.linked_archive_id ??
    value?.linked_archive?.archive_id ??
    value?.linked_report?.archive_id ??
    null
  );
}

function getLinkedReportId(value) {
  return value?.report_id ?? value?.linked_report?.report_id ?? null;
}

function getLinkedSubmissionId(value) {
  return value?.submission_id ?? value?.linked_submission?.submission_id ?? null;
}

export default function EvidenceAuditPage({
  apiBaseUrl,
  getAccessToken,
  tenantId: tenantIdProp = "",
  userId: userIdProp = "",
  routeParams = {},
  setMode,
  themeMode = "dark",
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();
  const resolvedThemeMode =
    String(themeMode || "").trim().toLowerCase() === "light" ? "light" : "dark";
  const dashboardThemeClass = resolvedThemeMode === "dark" ? " dark" : "";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [panelWarnings, setPanelWarnings] = useState([]);
  const [archiveFilter, setArchiveFilter] = useState("trusted");

  const [archiveItems, setArchiveItems] = useState([]);
  const [reports, setReports] = useState([]);
  const [exportsList, setExportsList] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  const [selectedArchiveId, setSelectedArchiveId] = useState(null);
  const [visualSelectedArchiveId, setVisualSelectedArchiveId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedExportId, setSelectedExportId] = useState(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [linkedJumpArchiveId, setLinkedJumpArchiveId] = useState(null);
  const [advisorFocus, setAdvisorFocus] = useState(null);

  const [archiveDetail, setArchiveDetail] = useState(null);
  const [archiveVerification, setArchiveVerification] = useState(null);
  const [selectedExportDetail, setSelectedExportDetail] = useState(null);
  const [selectedSubmissionDetail, setSelectedSubmissionDetail] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);

  const [evidenceExplainOpen, setEvidenceExplainOpen] = useState(false);
  const [evidenceExplainQuestion, setEvidenceExplainQuestion] = useState(
    EVIDENCE_EXPLAIN_PROMPTS[0]
  );
  const [evidenceExplainCard, setEvidenceExplainCard] = useState(null);
  const [evidenceExplainLoading, setEvidenceExplainLoading] = useState(false);
  const [evidenceExplainError, setEvidenceExplainError] = useState("");

  const archiveSectionRef = useRef(null);
  const reportsSectionRef = useRef(null);
  const exportsSectionRef = useRef(null);
  const submissionsSectionRef = useRef(null);

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId = String(tenantIdProp || "").trim();
  const userId = String(userIdProp || "").trim();

  const routeSelectionContext = useMemo(
    () => ({
      sourcePage: normalizeRouteValue(routeParams.sourcePage || routeParams.source || ""),
      framework: normalizeRouteValue(routeParams.framework || ""),
      controlId: normalizeRouteValue(routeParams.controlId || routeParams.control_id || ""),
      ruleId: normalizeRouteValue(routeParams.ruleId || routeParams.rule_id || ""),
      resourceId: normalizeRouteValue(routeParams.resourceId || routeParams.resource_id || ""),
      evidenceRef: normalizeRouteValue(routeParams.evidenceRef || routeParams.evidence_ref || ""),
      archiveId: normalizeRouteValue(routeParams.archiveId || routeParams.archive_id || ""),
      intervalId: normalizeRouteValue(routeParams.intervalId || routeParams.interval_id || ""),
      sourceRef: normalizeRouteValue(routeParams.sourceRef || routeParams.source_ref || ""),
    }),
    [routeParams]
  );

  const pageHasScopedContext = useMemo(
    () => hasScopedContext(routeSelectionContext),
    [routeSelectionContext]
  );

  useEffect(() => {
    const routeArchiveId = String(routeSelectionContext.archiveId || "").trim();
    const effectiveFocus =
      advisorFocus ||
      (routeArchiveId
        ? {
            record_type: "archive",
            archive_id: routeArchiveId,
            focus_label: `Archive ${routeArchiveId}`,
            focus_type: "Archive",
          }
        : null);

    const pageContext = {
      page: "evidence",
      surface: "evidence_page",
      intelligence_mode: effectiveFocus ? "contextual_explain" : "page",
      ...(routeSelectionContext.framework
        ? { framework: routeSelectionContext.framework }
        : {}),
      ...(routeSelectionContext.sourcePage
        ? { section: routeSelectionContext.sourcePage }
        : {}),
      ...(archiveFilter ? { filter: archiveFilter } : {}),
    };

    clearCopilotContext();

    if (!effectiveFocus) {
      setCopilotContext(pageContext);
      return () => clearCopilotContext();
    }

    setCopilotContext({
      ...pageContext,
      ...effectiveFocus,
    });
    return () => clearCopilotContext();
  }, [
    advisorFocus,
    archiveFilter,
    clearCopilotContext,
    routeSelectionContext.archiveId,
    routeSelectionContext.framework,
    routeSelectionContext.sourcePage,
    setCopilotContext,
  ]);

  useEffect(() => {
    setMode("evidence", {
      framework: routeSelectionContext.framework || undefined,
      controlId: routeSelectionContext.controlId || undefined,
      ruleId: routeSelectionContext.ruleId || undefined,
      resourceId: routeSelectionContext.resourceId || undefined,
      evidenceRef: routeSelectionContext.evidenceRef || undefined,
      archiveId: routeSelectionContext.archiveId || undefined,
      sourcePage: routeSelectionContext.sourcePage || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    routeSelectionContext.framework,
    routeSelectionContext.controlId,
    routeSelectionContext.ruleId,
    routeSelectionContext.resourceId,
    routeSelectionContext.evidenceRef,
    routeSelectionContext.archiveId,
    routeSelectionContext.sourcePage,
  ]);

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") return await getAccessToken();
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  const requestJson = useCallback(
    async (path, options = {}) => {
      const token = await fetchToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("evidence-intelligence-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const response = await fetch(`${normalizedBase}${path}`, {
          method: options.method || "GET",
          headers,
          body: options.body ? JSON.stringify(options.body) : undefined,
          signal: controller.signal,
        });
        if (!response.ok) {
          const responseText = await response.text().catch(() => "");
          throw new Error(`${response.status} ${response.statusText}: ${responseText}`);
        }
        return response.json();
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    [fetchToken, normalizedBase, tenantId, userId]
  );

  const loadReportsOnly = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("evidence-audit-page-timeout"),
      REQUEST_TIMEOUT_MS
    );

    try {
      setReportsLoading(true);
      const token = await fetchToken();
      const value = await getJsonWithToken(token, "/evidence/reports/recent", {}, controller.signal);
      const nextReports = normalizeList(value, ["reports", "report_packs", "packs"]);
      setReports(nextReports);

      if (!selectedReportId && nextReports.length > 0) {
        setSelectedReportId(nextReports[0]?.id || nextReports[0]?.pack_id || null);
      }

      setPanelWarnings((prev) =>
        prev.filter((warning) => !warning.toLowerCase().startsWith("report packs unavailable:"))
      );
    } catch (e) {
      const message =
        e?.name === "AbortError"
          ? `Report packs unavailable: timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
          : `Report packs unavailable: ${String(e?.message || e)}`;

      setPanelWarnings((prev) => {
        const withoutReports = prev.filter(
          (warning) => !warning.toLowerCase().startsWith("report packs unavailable:")
        );
        return [...withoutReports, message];
      });
    } finally {
      clearTimeout(timeoutId);
      controller.abort();
      setReportsLoading(false);
    }
  }, [fetchToken, getJsonWithToken, selectedReportId]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr(null);
    setPanelWarnings([]);

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("evidence-audit-page-timeout"),
      REQUEST_TIMEOUT_MS
    );

    (async () => {
      try {
        const token = await fetchToken();

        const requests = [
          {
            key: "archiveRecent",
            label: "Archive items",
            required: false,
            promise: getJsonWithToken(token, "/evidence/archive/recent", {}, controller.signal),
          },
          {
            key: "reportsRecent",
            label: "Report packs",
            required: false,
            promise: getJsonWithToken(token, "/evidence/reports/recent", {}, controller.signal),
          },
          {
            key: "exportsRecent",
            label: "Audit exports",
            required: false,
            promise: getJsonWithToken(
              token,
              "/evidence/audit/exports/recent",
              {},
              controller.signal
            ),
          },
          {
            key: "submissionsRecent",
            label: "Submissions",
            required: false,
            promise: getJsonWithToken(token, "/evidence/submissions/recent", {}, controller.signal),
          },
        ];

        const settled = await Promise.allSettled(requests.map((r) => r.promise));
        if (!mounted) return;

        let requiredFailure = null;
        const nextWarnings = [];

        let nextArchiveItems = [];
        let nextReports = [];
        let nextExports = [];
        let nextSubmissions = [];

        settled.forEach((result, index) => {
          const req = requests[index];

          if (result.status === "fulfilled") {
            const value = result.value;
            if (req.key === "archiveRecent") {
              nextArchiveItems = normalizeList(value, ["archives", "archive_items", "evidence"]);
            }
            if (req.key === "reportsRecent") {
              nextReports = normalizeList(value, ["reports", "report_packs", "packs"]);
            }
            if (req.key === "exportsRecent") {
              nextExports = normalizeList(value, ["exports", "audit_exports"]);
            }
            if (req.key === "submissionsRecent") {
              nextSubmissions = normalizeList(value, ["submissions", "regulator_submissions"]);
            }
            return;
          }

          const reason = result.reason;
          const message =
            reason?.name === "AbortError"
              ? `${req.label} timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
              : String(reason?.message || reason || `${req.label} failed.`);

          if (req.required) {
            requiredFailure = `${req.label} failed: ${message}`;
          } else {
            nextWarnings.push(`${req.label} unavailable: ${message}`);
          }
        });

        if (requiredFailure) throw new Error(requiredFailure);

        setArchiveItems(nextArchiveItems);
        setReports(nextReports);
        setExportsList(nextExports);
        setSubmissions(nextSubmissions);

        const matchedArchive =
          nextArchiveItems.find((item) => archiveMatchesContext(item, routeSelectionContext)) ||
          nextArchiveItems[0] ||
          null;

        const matchedReport =
          nextReports.find((item) => reportMatchesContext(item, routeSelectionContext)) ||
          nextReports[0] ||
          null;

        const matchedExport = nextExports[0] || null;
        const matchedSubmission = nextSubmissions[0] || null;

        const nextArchiveSelection = matchedArchive?.id || matchedArchive?.archive_id || null;
        setSelectedArchiveId(nextArchiveSelection);
        setVisualSelectedArchiveId(nextArchiveSelection);
        setSelectedReportId(matchedReport?.id || matchedReport?.pack_id || null);
        setSelectedExportId(matchedExport?.id || matchedExport?.export_id || null);
        setSelectedSubmissionId(matchedSubmission?.id || matchedSubmission?.submission_id || null);

        if (
          pageHasScopedContext &&
          !nextArchiveItems.some((item) => archiveMatchesContext(item, routeSelectionContext))
        ) {
          nextWarnings.push(
            "No direct archive match found for current context. Showing recent related evidence."
          );
        }

        setPanelWarnings(nextWarnings);
      } catch (e) {
        if (!mounted) return;
        console.error("Evidence & Audit page load error:", e);
        setErr(String(e?.message || e));
      } finally {
        clearTimeout(timeoutId);
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchToken, getJsonWithToken, routeSelectionContext, pageHasScopedContext]);

  useEffect(() => {
    let mounted = true;

    if (!selectedArchiveId) {
      setArchiveDetail(null);
      setArchiveVerification(null);
      return () => {
        mounted = false;
      };
    }

    const controller = new AbortController();

    (async () => {
      try {
        const token = await fetchToken();

        const [detailRes, verifyRes] = await Promise.allSettled([
          getJsonWithToken(token, `/evidence/archive/${selectedArchiveId}`, {}, controller.signal),
          getJsonWithToken(
            token,
            `/evidence/archive/${selectedArchiveId}/verify`,
            {},
            controller.signal
          ),
        ]);

        if (!mounted) return;

        if (detailRes.status === "fulfilled") {
          setArchiveDetail(detailRes.value || null);
        } else {
          setArchiveDetail(null);
        }

        if (verifyRes.status === "fulfilled") {
          setArchiveVerification(verifyRes.value || null);
        } else {
          setArchiveVerification(null);
        }
      } catch (e) {
        if (!mounted) return;
        setArchiveDetail(null);
        setArchiveVerification(null);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [selectedArchiveId, fetchToken, getJsonWithToken]);

  useEffect(() => {
    let mounted = true;

    if (!selectedExportId) {
      setSelectedExportDetail(null);
      return () => {
        mounted = false;
      };
    }

    const controller = new AbortController();

    (async () => {
      try {
        const token = await fetchToken();
        const data = await getJsonWithToken(
          token,
          `/evidence/audit/exports/${selectedExportId}`,
          {},
          controller.signal
        );
        if (!mounted) return;
        setSelectedExportDetail(data || null);
      } catch (e) {
        if (!mounted) return;
        setSelectedExportDetail(null);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [selectedExportId, fetchToken, getJsonWithToken]);

  useEffect(() => {
    let mounted = true;

    if (!selectedSubmissionId) {
      setSelectedSubmissionDetail(null);
      return () => {
        mounted = false;
      };
    }

    const controller = new AbortController();

    (async () => {
      try {
        const token = await fetchToken();
        const data = await getJsonWithToken(
          token,
          `/evidence/submissions/${selectedSubmissionId}`,
          {},
          controller.signal
        );
        if (!mounted) return;
        setSelectedSubmissionDetail(data || null);
      } catch (e) {
        if (!mounted) return;
        setSelectedSubmissionDetail(null);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [selectedSubmissionId, fetchToken, getJsonWithToken]);

  const selectedArchive = useMemo(() => {
    return (
      archiveItems.find(
        (item) => String(item.id || item.archive_id || "") === String(selectedArchiveId || "")
      ) || null
    );
  }, [archiveItems, selectedArchiveId]);

  const selectedReport = useMemo(() => {
    return (
      reports.find(
        (item) => String(item.id || item.pack_id || "") === String(selectedReportId || "")
      ) || null
    );
  }, [reports, selectedReportId]);

  const selectedExport = useMemo(() => {
    return (
      exportsList.find(
        (item) => String(item.id || item.export_id || "") === String(selectedExportId || "")
      ) || null
    );
  }, [exportsList, selectedExportId]);

  const selectedSubmission = useMemo(() => {
    return (
      submissions.find(
        (item) => String(item.id || item.submission_id || "") === String(selectedSubmissionId || "")
      ) || null
    );
  }, [submissions, selectedSubmissionId]);

  const selectedArchiveStatus = useMemo(
    () => deriveSelectedArchiveStatus(selectedArchive, archiveDetail, archiveVerification),
    [selectedArchive, archiveDetail, archiveVerification]
  );

  const selectedArchiveNarrative = useMemo(
    () => buildSelectedArchiveNarrative(selectedArchive, archiveDetail, archiveVerification),
    [selectedArchive, archiveDetail, archiveVerification]
  );

  const selectedArchiveExplainLabel = useMemo(() => {
    const archiveId =
      archiveDetail?.archive_id ||
      selectedArchive?.archive_id ||
      archiveDetail?.id ||
      selectedArchive?.id ||
      "selected";
    const qualifier =
      archiveDetail?.rule_id ||
      selectedArchive?.rule_id ||
      archiveDetail?.control_id ||
      selectedArchive?.control_id ||
      archiveDetail?.resource_id ||
      selectedArchive?.resource_id ||
      archiveDetail?.framework ||
      selectedArchive?.framework ||
      "";
    return qualifier ? `Archive ${archiveId} · ${qualifier}` : `Archive ${archiveId}`;
  }, [archiveDetail, selectedArchive]);

  const askEvidenceExplain = useCallback(
    async (question) => {
      if (!selectedArchiveId) return;

      setEvidenceExplainQuestion(question);
      setEvidenceExplainLoading(true);
      setEvidenceExplainError("");

      try {
        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          body: {
            scope: "evidence",
            question,
            copilot_context: {
              page: "evidence_audit",
              surface: "evidence_page",
              intelligence_mode: "contextual_explain",
              record_type: "archive",
              archive_id: selectedArchiveId,
              framework:
                archiveDetail?.framework || selectedArchive?.framework || undefined,
              control_id:
                archiveDetail?.control_id || selectedArchive?.control_id || undefined,
              rule_id:
                archiveDetail?.rule_id || selectedArchive?.rule_id || undefined,
              resource_id:
                archiveDetail?.resource_id || selectedArchive?.resource_id || undefined,
              evidence_ref:
                archiveDetail?.artifact_uri ||
                archiveDetail?.evidence_ref ||
                selectedArchive?.artifact_uri ||
                selectedArchive?.evidence_ref ||
                undefined,
            },
          },
        });

        if (!payload?.card || typeof payload.card !== "object") {
          throw new Error("Evidence Intelligence returned no grounded explanation card.");
        }

        setEvidenceExplainCard(payload.card);
      } catch (error) {
        console.error("Evidence contextual explain failed:", error);
        setEvidenceExplainError(
          String(error?.message || error || "Unable to explain this evidence.")
        );
      } finally {
        setEvidenceExplainLoading(false);
      }
    },
    [archiveDetail, requestJson, selectedArchive, selectedArchiveId]
  );

  const openEvidenceExplain = useCallback(() => {
    if (!selectedArchiveId) return;
    setEvidenceExplainOpen(true);
    setEvidenceExplainCard(null);
    setEvidenceExplainError("");
    setEvidenceExplainQuestion(EVIDENCE_EXPLAIN_PROMPTS[0]);
    askEvidenceExplain(EVIDENCE_EXPLAIN_PROMPTS[0]);
  }, [askEvidenceExplain, selectedArchiveId]);

  const closeEvidenceExplain = useCallback(() => {
    setEvidenceExplainOpen(false);
  }, []);


  const chainStatus = useMemo(() => {
    if (archiveVerification?.chain_valid === true) return { label: "Chain Valid", tone: "ok" };
    if (archiveVerification?.chain_valid === false) return { label: "Chain Invalid", tone: "danger" };
    return { label: "Chain Unknown", tone: "default" };
  }, [archiveVerification]);

  const archiveFilterCounts = useMemo(() => {
    const counts = {
      all: archiveItems.length,
      trusted: 0,
      verified: 0,
      s3: 0,
      legacy: 0,
      missing: 0,
    };

    archiveItems.forEach((item) => {
      const verified = deriveArchiveStatus(item).label === "Verified";
      const s3 = isS3BackedArchive(item);
      const missing = isMissingArchive(item);
      const trusted = s3 && !missing;

      if (trusted) counts.trusted += 1;
      if (verified) counts.verified += 1;
      if (s3) counts.s3 += 1;
      if (!s3) counts.legacy += 1;
      if (missing) counts.missing += 1;
    });

    return counts;
  }, [archiveItems]);

  const sortedFilteredArchiveItems = useMemo(() => {
    const filtered = archiveItems.filter((item) => {
      const verified = deriveArchiveStatus(item).label === "Verified";
      const s3 = isS3BackedArchive(item);
      const missing = isMissingArchive(item);
      const trusted = s3 && !missing;

      if (archiveFilter === "trusted") return trusted;
      if (archiveFilter === "verified") return verified;
      if (archiveFilter === "s3") return s3;
      if (archiveFilter === "legacy") return !s3;
      if (archiveFilter === "missing") return missing;
      return true;
    });

    return [...filtered].sort((a, b) => {
      const scoreA = archivePriorityScore(a);
      const scoreB = archivePriorityScore(b);
      return scoreB - scoreA;
    });
  }, [archiveItems, archiveFilter]);

  useEffect(() => {
    if (!sortedFilteredArchiveItems.length) {
      setVisualSelectedArchiveId(null);
      return;
    }

    const visualStillVisible = sortedFilteredArchiveItems.some(
      (item) =>
        String(item.id || item.archive_id || "") === String(visualSelectedArchiveId || "")
    );

    if (!visualStillVisible) {
      const fallbackId =
        selectedArchiveId &&
        sortedFilteredArchiveItems.some(
          (item) =>
            String(item.id || item.archive_id || "") === String(selectedArchiveId || "")
        )
          ? selectedArchiveId
          : sortedFilteredArchiveItems[0]?.id || sortedFilteredArchiveItems[0]?.archive_id || null;

      setVisualSelectedArchiveId(fallbackId);
    }
  }, [sortedFilteredArchiveItems, visualSelectedArchiveId, selectedArchiveId]);

  useEffect(() => {
    if (!linkedJumpArchiveId) return undefined;

    const timer = window.setTimeout(() => {
      setLinkedJumpArchiveId(null);
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [linkedJumpArchiveId]);

  const proofMetrics = useMemo(() => {
    return {
      archiveCount: archiveItems.length,
      trustedCount: archiveFilterCounts.trusted,
      selectedTrust: selectedArchiveStatus.label,
      selectedTrustTone: selectedArchiveStatus.tone,
      chainLabel: chainStatus.label,
      chainTone: chainStatus.tone,
      reportCount: reports.length,
      exportCount: exportsList.length,
      submissionCount: submissions.length,
      latestVerificationTs:
        archiveVerification?.verified_at ||
        archiveVerification?.timestamp ||
        archiveItems
          .map((item) => item.verified_at || item.updated_at || item.created_at)
          .filter(Boolean)
          .sort()
          .slice(-1)[0] ||
        null,
    };
  }, [
    archiveItems,
    reports,
    exportsList,
    submissions,
    selectedArchiveStatus,
    chainStatus,
    archiveVerification,
    archiveFilterCounts,
  ]);

  const contextBannerTitle = useMemo(
    () =>
      pageHasScopedContext
        ? buildContextSourceLabel(routeSelectionContext)
        : "Direct Evidence View",
    [routeSelectionContext, pageHasScopedContext]
  );


  const openLinkedArchive = useCallback(
    (value) => {
      const archiveId = getLinkedArchiveId(value);
      if (!archiveId) return;

      const nextId = String(archiveId);
      setLinkedJumpArchiveId(nextId);
      setVisualSelectedArchiveId(nextId);
      setSelectedArchiveId(nextId);
      setAdvisorFocus({
        record_type: "archive",
        archive_id: nextId,
        focus_label: `Archive ${nextId}`,
        focus_type: "Archive",
      });

      window.requestAnimationFrame(() => {
        const section = archiveSectionRef?.current;
        if (!section) return;

        const top = section.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: "smooth",
        });
      });
    },
    []
  );

  const openLinkedReport = useCallback(
    (value) => {
      const reportId = getLinkedReportId(value);
      if (!reportId) return;
      const nextId = String(reportId);
      setSelectedReportId(nextId);
      setAdvisorFocus({
        record_type: "report",
        report_id: nextId,
        focus_label: `Report pack ${nextId}`,
        focus_type: "Report Pack",
      });
      scrollToSection(reportsSectionRef);
    },
    []
  );

  const openLinkedSubmission = useCallback(
    (value) => {
      const submissionId = getLinkedSubmissionId(value);
      if (!submissionId) return;
      const nextId = String(submissionId);
      setSelectedSubmissionId(nextId);
      setAdvisorFocus({
        record_type: "regulator_submission",
        submission_id: nextId,
        focus_label: `Regulator submission ${nextId}`,
        focus_type: "Regulator Submission",
      });
      scrollToSection(submissionsSectionRef);
    },
    []
  );




  if (loading) {
    return (
      <div className={`oasys-dashboard${dashboardThemeClass} evidence-audit-page`}>
        <div className="dash-shell">
          <div className="dash-header">
            <div>
              <div className="dash-title">Evidence & Audit</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            Loading evidence and audit proof…
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className={`oasys-dashboard${dashboardThemeClass} evidence-audit-page`}>
        <div className="dash-shell">
          <div className="dash-header">
            <div>
              <div className="dash-title">Evidence & Audit</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ color: "#ff7b7b" }}>Failed to load evidence & audit: {err}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`oasys-dashboard${dashboardThemeClass} evidence-audit-page`}>
      <div className="dash-shell">
        <header className="dash-header evidence-page-header">
          <div className="evidence-page-header__copy">
            <h1 className="evidence-page-header__title">Evidence & Audit</h1>
            <p className="evidence-page-header__description">
              Review archived evidence, verify integrity, inspect report packs, audit exports, and
              regulator submission trail.
            </p>
          </div>

          <div className="dash-header-actions evidence-page-header__actions">
            <OperationalCaptureAction label="Capture New Evidence" apiBaseUrl={normalizedBase}
              getAccessToken={fetchToken} tenantId={tenantId} userId={userId} discoverConnector />
            <button className="btn btn-sm" onClick={() => setMode("dashboard")}>
              Mission Control
            </button>
            <button
              className="btn btn-sm"
              onClick={() =>
                setMode("drift", {
                  framework: routeSelectionContext.framework || undefined,
                  controlId: routeSelectionContext.controlId || undefined,
                  ruleId: routeSelectionContext.ruleId || undefined,
                  resourceId: routeSelectionContext.resourceId || undefined,
                  evidenceRef: routeSelectionContext.evidenceRef || undefined,
                })
              }
            >
              Open Drift & State
            </button>
          </div>
        </header>

        {pageHasScopedContext ? (
        <section className="card evidence-context-card">
          <div className="card-header">
            <div className="card-title">{contextBannerTitle}</div>
          </div>
          <div className="card-body" style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <ProofBadge
                label={
                  pageHasScopedContext
                    ? cleanValue(routeSelectionContext.sourcePage, "Context")
                    : "Direct"
                }
              />
              {routeSelectionContext.evidenceRef ? (
                <ProofBadge label="Artifact URI" tone="ok" />
              ) : null}
              {routeSelectionContext.archiveId ? (
                <ProofBadge label="Archive Hint" tone="warn" />
              ) : null}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              <MetaField label="Framework" value={routeSelectionContext.framework} />
              <MetaField label="Control" value={routeSelectionContext.controlId} />
              <MetaField label="Rule" value={routeSelectionContext.ruleId} />
              <MetaField label="Resource" value={routeSelectionContext.resourceId} wrap />
              <MetaField label="Artifact URI" value={routeSelectionContext.evidenceRef} wrap />
              <MetaField label="Interval" value={routeSelectionContext.intervalId} />
            </div>
          </div>
        </section>
        ) : null}

        <EvidenceIntelligencePanel requestJson={requestJson} />

        <div ref={reportsSectionRef} id="report-packs" style={{ scrollMarginTop: 96 }}><AuditReportsPackages reports={reports} loading={reportsLoading} onRefresh={loadReportsOnly} apiBaseUrl={normalizedBase} getAccessToken={fetchToken} tenantId={tenantId} userId={userId} /></div>

        {panelWarnings.length > 0 ? (
          <div
            className="card"
            style={{
              padding: "10px 14px",
              marginBottom: 16,
              display: "grid",
              gap: 6,
            }}
          >
            <div style={{ color: "var(--warn)", fontWeight: 700, fontSize: 13 }}>
              Evidence & Audit loaded with partial data
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                color: "var(--muted)",
                fontSize: 12,
              }}
            >
              {panelWarnings.map((warning, index) => (
                <span
                  key={`${warning}-${index}`}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    background: "color-mix(in srgb, var(--card), transparent 12%)",
                  }}
                >
                  {warning}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <section className="card evidence-proof-overview">
          <div className="card-header">
            <div className="card-title">Proof Overview</div>
          </div>
          <div className="card-body">
            <div className="hero-kpis evidence-proof-metrics">
              <MetricCard
                label="Archive Items"
                value={proofMetrics.archiveCount}
                footnote="recent archive surface"
                onClick={() => scrollToSection(archiveSectionRef)}
              />
              <MetricCard
                label="Trusted Proof"
                value={proofMetrics.trustedCount}
                footnote="S3-backed current records"
                tone="ok"
                onClick={() => {
                  setArchiveFilter("trusted");
                  scrollToSection(archiveSectionRef);
                }}
              />
              <MetricCard
                label="Selected Trust"
                value={proofMetrics.selectedTrust}
                footnote="authoritative current check"
                tone={proofMetrics.selectedTrustTone}
                onClick={() => scrollToSection(archiveSectionRef)}
              />
              <MetricCard
                label="Chain Status"
                value={proofMetrics.chainLabel}
                footnote="tamper-evident continuity"
                tone={proofMetrics.chainTone}
                onClick={() => scrollToSection(archiveSectionRef)}
              />
              <MetricCard
                label="Report Packs"
                value={proofMetrics.reportCount}
                footnote="auditor-ready bundles"
                onClick={() => scrollToSection(reportsSectionRef)}
              />
              <MetricCard
                label="Submissions"
                value={proofMetrics.submissionCount}
                footnote={formatTimestamp(proofMetrics.latestVerificationTs)}
                onClick={() => scrollToSection(submissionsSectionRef)}
              />
            </div>
          </div>
        </section>

        <div className="dash-grid evidence-audit-workspace">
          <div className="content-column evidence-audit-workspace__lists">
            <section
              className="section-card evidence-pair-list evidence-pair-list--archive"
              ref={archiveSectionRef}
              id="archive-items"
              style={{ scrollMarginTop: 96 }}
            >
              <div className="section-header">
                <div>
                  <div className="section-title">Archive Items</div>
                  <div className="section-subtitle">
                    Trusted proof is shown first. Legacy and missing-artifact records remain
                    available for context but should not be treated as the primary audit surface.
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "0 0 14px 0" }}>
                <FilterChip
                  label={`Trusted (${archiveFilterCounts.trusted})`}
                  active={archiveFilter === "trusted"}
                  onClick={() => setArchiveFilter("trusted")}
                  tone="ok"
                />
                <FilterChip
                  label={`All (${archiveFilterCounts.all})`}
                  active={archiveFilter === "all"}
                  onClick={() => setArchiveFilter("all")}
                />
                <FilterChip
                  label={`Verified (${archiveFilterCounts.verified})`}
                  active={archiveFilter === "verified"}
                  onClick={() => setArchiveFilter("verified")}
                  tone="ok"
                />
                <FilterChip
                  label={`S3-backed (${archiveFilterCounts.s3})`}
                  active={archiveFilter === "s3"}
                  onClick={() => setArchiveFilter("s3")}
                  tone="ok"
                />
                <FilterChip
                  label={`Legacy (${archiveFilterCounts.legacy})`}
                  active={archiveFilter === "legacy"}
                  onClick={() => setArchiveFilter("legacy")}
                  tone="warn"
                />
                <FilterChip
                  label={`Missing (${archiveFilterCounts.missing})`}
                  active={archiveFilter === "missing"}
                  onClick={() => setArchiveFilter("missing")}
                  tone="warn"
                />
              </div>

              <div className="tasks-list">
                {sortedFilteredArchiveItems.length === 0 ? (
                  <div className="muted" style={{ padding: 18 }}>
                    No archive items match the current filter.
                  </div>
                ) : (
                  sortedFilteredArchiveItems.map((item, idx) => {
                    const archiveId = item.id || item.archive_id || `archive-${idx}`;
                    const selected = String(archiveId) === String(visualSelectedArchiveId || "");
                    const matched = archiveMatchesContext(item, routeSelectionContext);
                    const status = deriveArchiveSurfaceStatus(
                      item,
                      String(archiveId) === String(selectedArchiveId || ""),
                      selectedArchiveStatus
                    );
                    const sourceBadges = buildArchiveSourceBadges(item);

                    const linkedJumped =
                      String(archiveId) === String(linkedJumpArchiveId || "");

                    return (
                      <button
                        key={archiveId}
                        type="button"
                        className="task-row"
                        onClick={() => {
                          const nextId = String(archiveId);
                          setLinkedJumpArchiveId(null);
                          setVisualSelectedArchiveId(nextId);
                          setSelectedArchiveId(nextId);
                          setAdvisorFocus({
                            record_type: "archive",
                            archive_id: nextId,
                            focus_label: `Archive ${nextId}`,
                            focus_type: "Archive",
                          });
                        }}
                        style={{
                          textAlign: "left",
                          width: "100%",
                          cursor: "pointer",
                          borderColor: linkedJumped
                            ? "color-mix(in srgb, var(--ok), transparent 20%)"
                            : selected
                            ? "var(--ring)"
                            : undefined,
                          boxShadow: linkedJumped
                            ? "0 0 0 2px color-mix(in srgb, var(--ok), transparent 48%), 0 0 0 8px color-mix(in srgb, var(--ok), transparent 94%)"
                            : selected
                            ? "0 0 0 1px color-mix(in srgb, var(--ring), transparent 52%)"
                            : "none",
                          borderLeft: linkedJumped
                            ? "3px solid var(--ok)"
                            : selected
                            ? "2px solid var(--brand)"
                            : matched
                            ? "2px solid var(--ok)"
                            : "2px solid transparent",
                          transform: linkedJumped ? "translateY(-1px)" : "none",
                          transition: "box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease",
                          background: linkedJumped
                            ? "color-mix(in srgb, var(--ok), transparent 95%)"
                            : undefined,
                        }}
                      >
                        <div className="task-main">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                              marginBottom: 4,
                            }}
                          >
                            {selected ? (
                              <>
                                <ProofBadge label="Selected" tone="default" strong />
                                <ProofBadge label={status.label} tone={status.tone} />
                              </>
                            ) : (
                              <ProofBadge label={status.label} tone={status.tone} />
                            )}

                            {sourceBadges.map((badge) => (
                              <ProofBadge
                                key={`${archiveId}-${badge.label}`}
                                label={badge.label}
                                tone={badge.tone}
                              />
                            ))}

                            {matched && !selected ? (
                              <ProofBadge label="Context Match" tone="ok" />
                            ) : null}

                            {linkedJumped ? (
                              <ProofBadge label="Jump Target" tone="ok" strong />
                            ) : null}
                          </div>

                          <div className="task-title">
                            {cleanValue(item.archive_id || item.id, "Archive Record")}
                          </div>

                          <div className="task-meta">
                            <span>{cleanValue(item.framework)}</span>
                            <span>{cleanValue(item.evidence_ref)}</span>
                            <span>{formatRelativeTime(item.created_at || item.updated_at)}</span>
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--muted)",
                              marginTop: 4,
                              lineHeight: 1.45,
                            }}
                          >
                            Source: {cleanValue(item.source)} · Resource:{" "}
                            {cleanValue(item.resource_id)} · Retention:{" "}
                            {cleanValue(item.retention_mode || item.retention)}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </section>

            <section
              className="section-card evidence-pair-list evidence-pair-list--exports"
              ref={exportsSectionRef}
              id="audit-exports"
              style={{ scrollMarginTop: 96 }}
            >
              <div className="section-header">
                <div>
                  <div className="section-title">Audit Exports</div>
                  <div className="section-subtitle">
                    Recent audit export runs and linked detail when available.
                  </div>
                </div>
              </div>

              <div className="tasks-list">
                {exportsList.length === 0 ? (
                  <div className="muted" style={{ padding: 18 }}>
                    No audit exports were returned.
                  </div>
                ) : (
                  exportsList.map((item, idx) => {
                    const exportId = item.id || item.export_id || `export-${idx}`;
                    const selected = String(exportId) === String(selectedExportId);

                    return (
                      <button
                        key={exportId}
                        type="button"
                        className="task-row"
                        onClick={() => {
                          const nextId = String(exportId);
                          setSelectedExportId(nextId);
                          setAdvisorFocus({
                            record_type: "audit_export",
                            audit_export_id: nextId,
                            focus_label: `Audit export ${nextId}`,
                            focus_type: "Audit Export",
                          });
                        }}
                        style={{
                          textAlign: "left",
                          width: "100%",
                          cursor: "pointer",
                          borderColor: selected ? "var(--ring)" : undefined,
                          boxShadow: selected
                            ? "0 0 0 1px color-mix(in srgb, var(--ring), transparent 52%)"
                            : "none",
                          borderLeft: selected ? "2px solid var(--brand)" : "2px solid transparent",
                        }}
                      >
                        <div className="task-main">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                              marginBottom: 4,
                            }}
                          >
                            <ProofBadge label="Export" tone="default" />
                            {getLinkedArchiveId(item) ? (
                              <ProofBadge label="Linked Archive" tone="ok" />
                            ) : null}
                            {getLinkedReportId(item) ? (
                              <ProofBadge label="Linked Report" tone="ok" />
                            ) : null}
                            {getLinkedSubmissionId(item) ? (
                              <ProofBadge label="Linked Submission" tone="ok" />
                            ) : null}
                            {selected ? <ProofBadge label="Selected" tone="default" strong /> : null}
                          </div>

                          <div className="task-title">
                            {cleanValue(item.export_id || item.id, "Audit Export")}
                          </div>
                          <div className="task-meta">
                            <span>{cleanValue(item.framework)}</span>
                            <span>{formatRelativeTime(item.created_at)}</span>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                            Included sections:{" "}
                            {joinIncludedSections(item.included_sections || item.sections)}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

            </section>

            <section
              className="section-card evidence-pair-list evidence-pair-list--submissions"
              ref={submissionsSectionRef}
              id="regulator-submissions"
              style={{ scrollMarginTop: 96 }}
            >
              <div className="section-header">
                <div>
                  <div className="section-title">Regulator Submissions</div>
                  <div className="section-subtitle">
                    Recent submission trail and linked pack/proof metadata.
                  </div>
                </div>
              </div>

              <div className="tasks-list">
                {submissions.length === 0 ? (
                  <div className="muted" style={{ padding: 18 }}>
                    No regulator submissions were returned.
                  </div>
                ) : (
                  submissions.map((item, idx) => {
                    const submissionId = item.id || item.submission_id || `submission-${idx}`;
                    const selected = String(submissionId) === String(selectedSubmissionId || "");
                    const matched = submissionMatchesContext(item, routeSelectionContext);

                    return (
                      <button
                        key={submissionId}
                        type="button"
                        className="task-row"
                        onClick={() => {
                          const nextId = String(submissionId);
                          setSelectedSubmissionId(nextId);
                          setAdvisorFocus({
                            record_type: "regulator_submission",
                            submission_id: nextId,
                            focus_label: `Regulator submission ${nextId}`,
                            focus_type: "Regulator Submission",
                          });
                        }}
                        style={{
                          textAlign: "left",
                          width: "100%",
                          cursor: "pointer",
                          borderColor: selected ? "var(--ring)" : undefined,
                          boxShadow: selected
                            ? "0 0 0 1px color-mix(in srgb, var(--ring), transparent 52%)"
                            : "none",
                          borderLeft: selected
                            ? "2px solid var(--brand)"
                            : matched
                            ? "2px solid var(--ok)"
                            : "2px solid transparent",
                        }}
                      >
                        <div className="task-main">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                              marginBottom: 4,
                            }}
                          >
                            <ProofBadge label={cleanValue(item.status || item.ack_status, "Unknown")} />
                            {item.linked_report?.pack_id ? <ProofBadge label="Linked Report" tone="ok" /> : null}
                            {item.archive_id ? <ProofBadge label="Linked Archive" tone="ok" /> : null}
                            {selected ? <ProofBadge label="Selected" tone="default" strong /> : null}
                          </div>

                          <div className="task-title">
                            {cleanValue(item.submission_id || item.id, "Submission")}
                          </div>

                          <div className="task-meta">
                            <span>{cleanValue(item.framework)}</span>
                            <span>{cleanValue(item.linked_pack_id || item.pack_id)}</span>
                            <span>{formatTimestamp(item.created_at)}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </section>
          </div>

          <aside className="filters-column evidence-audit-workspace__details">
            <section className="filters-card evidence-pair-detail evidence-pair-detail--archive" style={{ alignSelf: "start" }}>
              <div className="filters-header">
                <div className="filters-title">Verification Detail</div>
              </div>

              {selectedArchive || archiveDetail ? (
                <div className="filters-actions">
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <ProofBadge label={selectedArchiveStatus.label} tone={selectedArchiveStatus.tone} />
                    <ProofBadge label={chainStatus.label} tone={chainStatus.tone} />
                    {buildArchiveSourceBadges(archiveDetail || selectedArchive).map((badge) => (
                      <ProofBadge
                        key={`selected-${badge.label}`}
                        label={badge.label}
                        tone={badge.tone}
                      />
                    ))}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <div className="task-title" style={{ fontSize: 16 }}>
                      {cleanValue(
                        archiveDetail?.archive_id ||
                          selectedArchive?.archive_id ||
                          archiveDetail?.id ||
                          selectedArchive?.id,
                        "Archive Record"
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={openEvidenceExplain}
                      disabled={!selectedArchiveId}
                    >
                      Explain
                    </Button>
                  </div>

                  <div className="task-meta">
                    <span>{cleanValue(archiveDetail?.framework || selectedArchive?.framework)}</span>
                    <span>{cleanValue(archiveDetail?.source || selectedArchive?.source)}</span>
                  </div>

                  {String(selectedArchive?.id || selectedArchive?.archive_id || "") === String(linkedJumpArchiveId || "") ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ok)",
                        fontWeight: 700,
                        marginTop: 2,
                      }}
                    >
                      Jumped to linked archive record
                    </div>
                  ) : null}

                  <div
                    style={{
                      marginTop: 4,
                      padding: "10px 12px",
                      borderRadius: 12,
                      border:
                        selectedArchiveNarrative.tone === "danger"
                          ? "1px solid color-mix(in srgb, var(--danger), transparent 70%)"
                          : selectedArchiveNarrative.tone === "warn"
                          ? "1px solid color-mix(in srgb, var(--warn), transparent 70%)"
                          : selectedArchiveNarrative.tone === "ok"
                          ? "1px solid color-mix(in srgb, var(--ok), transparent 72%)"
                          : "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                      background:
                        selectedArchiveNarrative.tone === "danger"
                          ? "color-mix(in srgb, var(--danger), transparent 92%)"
                          : selectedArchiveNarrative.tone === "warn"
                          ? "color-mix(in srgb, var(--warn), transparent 92%)"
                          : selectedArchiveNarrative.tone === "ok"
                          ? "color-mix(in srgb, var(--ok), transparent 93%)"
                          : "color-mix(in srgb, var(--card), transparent 8%)",
                      color: "var(--text)",
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      {selectedArchiveNarrative.title}
                    </div>
                    <div>{selectedArchiveNarrative.body}</div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <MetaField
                      label="Artifact URI"
                      value={
                        archiveDetail?.artifact_uri ||
                        archiveDetail?.evidence_ref ||
                        selectedArchive?.artifact_uri ||
                        selectedArchive?.evidence_ref
                      }
                      wrap
                    />
                    <MetaField
                      label="Resource"
                      value={archiveDetail?.resource_id || selectedArchive?.resource_id}
                      wrap
                    />
                    <MetaField
                      label="SHA / Hash"
                      value={archiveVerification?.sha || archiveDetail?.sha || selectedArchive?.sha}
                      wrap
                    />
                    <MetaField
                      label="Verification"
                      value={
                        archiveVerification?.verification_status ||
                        archiveDetail?.verification_status ||
                        selectedArchive?.verification_status
                      }
                    />
                    <MetaField
                      label="Verified At"
                      value={formatTimestamp(
                        archiveVerification?.verified_at || archiveVerification?.timestamp
                      )}
                    />
                    <MetaField
                      label="Retention"
                      value={
                        archiveDetail?.retention_mode ||
                        selectedArchive?.retention_mode ||
                        archiveDetail?.retention ||
                        selectedArchive?.retention
                      }
                    />
                    <MetaField
                      label="Proof Bucket"
                      value={
                        archiveVerification?.resolved_bucket ||
                        archiveDetail?.s3_bucket ||
                        selectedArchive?.s3_bucket
                      }
                    />
                    <MetaField
                      label="Proof Key"
                      value={
                        archiveVerification?.resolved_key ||
                        archiveDetail?.s3_key ||
                        selectedArchive?.s3_key
                      }
                      wrap
                    />
                    <MetaField
                      label="Linked Report"
                      value={archiveVerification?.linked_report?.pack_id || archiveVerification?.report_pack_id || archiveDetail?.linked_report?.pack_id || archiveDetail?.report_pack_id}
                    />
                    <MetaField
                      label="Linked Submission"
                      value={archiveVerification?.submission_id || archiveDetail?.submission_id}
                    />
                  </div>

                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={() =>
                        setMode("posture", {
                          framework:
                            routeSelectionContext.framework ||
                            archiveDetail?.framework ||
                            selectedArchive?.framework ||
                            undefined,
                          controlId:
                            routeSelectionContext.controlId ||
                            archiveDetail?.control_id ||
                            selectedArchive?.control_id ||
                            undefined,
                          ruleId:
                            routeSelectionContext.ruleId ||
                            archiveDetail?.rule_id ||
                            selectedArchive?.rule_id ||
                            undefined,
                          resourceId:
                            routeSelectionContext.resourceId ||
                            archiveDetail?.resource_id ||
                            selectedArchive?.resource_id ||
                            undefined,
                        })
                      }
                    >
                      Open Posture
                    </button>

                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={() =>
                        setMode("alerts", {
                          framework:
                            routeSelectionContext.framework ||
                            archiveDetail?.framework ||
                            selectedArchive?.framework ||
                            undefined,
                          controlId:
                            routeSelectionContext.controlId ||
                            archiveDetail?.control_id ||
                            selectedArchive?.control_id ||
                            undefined,
                          ruleId:
                            routeSelectionContext.ruleId ||
                            archiveDetail?.rule_id ||
                            selectedArchive?.rule_id ||
                            undefined,
                          resourceId:
                            routeSelectionContext.resourceId ||
                            archiveDetail?.resource_id ||
                            selectedArchive?.resource_id ||
                            undefined,
                        })
                      }
                    >
                      Open Alerts
                    </button>

                    {getLinkedReportId(archiveVerification || archiveDetail || selectedArchive) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedReport(archiveVerification || archiveDetail || selectedArchive)}
                      >
                        Open Linked Report
                      </button>
                    ) : null}

                    {getLinkedSubmissionId(archiveVerification || archiveDetail || selectedArchive) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedSubmission(archiveVerification || archiveDetail || selectedArchive)}
                      >
                        Open Linked Submission
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="muted">Select an archive item to inspect verification detail.</div>
              )}
            </section>

            <section className="filters-card evidence-pair-detail evidence-pair-detail--exports" style={{ marginTop: 18 }}>
              <div className="filters-header">
                <div className="filters-title">Export Detail</div>
              </div>

              {selectedExport || selectedExportDetail ? (
                <div className="filters-actions">
                  <div className="task-title" style={{ fontSize: 15 }}>
                    {cleanValue(
                      selectedExportDetail?.export_id ||
                        selectedExport?.export_id ||
                        selectedExportDetail?.id ||
                        selectedExport?.id,
                      "Audit Export"
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <MetaField
                      label="Framework"
                      value={selectedExportDetail?.framework || selectedExport?.framework}
                    />
                    <MetaField
                      label="Created"
                      value={formatTimestamp(
                        selectedExportDetail?.created_at || selectedExport?.created_at
                      )}
                    />
                    <MetaField
                      label="Sections"
                      value={joinIncludedSections(
                        selectedExportDetail?.included_sections ||
                          selectedExport?.included_sections ||
                          selectedExportDetail?.sections ||
                          selectedExport?.sections
                      )}
                      wrap
                    />
                    <MetaField
                      label="Linked Archive"
                      value={selectedExportDetail?.archive_id || selectedExport?.archive_id}
                    />
                    <MetaField
                      label="Linked Report"
                      value={selectedExportDetail?.linked_report?.pack_id || selectedExport?.linked_report?.pack_id || selectedExportDetail?.report_pack_id || selectedExport?.report_pack_id || selectedExportDetail?.report_id || selectedExport?.report_id}
                    />
                    <MetaField
                      label="Linked Submission"
                      value={selectedExportDetail?.linked_submission?.submission_id || selectedExport?.linked_submission?.submission_id || selectedExportDetail?.submission_id || selectedExport?.submission_id}
                    />
                  </div>

                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    {getLinkedArchiveId(selectedExportDetail || selectedExport) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedArchive(selectedExportDetail || selectedExport)}
                      >
                        Open Linked Archive
                      </button>
                    ) : null}

                    {getLinkedReportId(selectedExportDetail || selectedExport) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedReport(selectedExportDetail || selectedExport)}
                      >
                        Open Linked Report
                      </button>
                    ) : null}

                    {getLinkedSubmissionId(selectedExportDetail || selectedExport) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedSubmission(selectedExportDetail || selectedExport)}
                      >
                        Open Linked Submission
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="muted">Select an audit export to inspect detail.</div>
              )}
            </section>

            <section className="filters-card evidence-pair-detail evidence-pair-detail--submissions" style={{ marginTop: 18 }}>
              <div className="filters-header">
                <div className="filters-title">Submission Detail</div>
              </div>

              {selectedSubmission || selectedSubmissionDetail ? (
                <div className="filters-actions">
                  <div className="task-title" style={{ fontSize: 15 }}>
                    {cleanValue(
                      selectedSubmissionDetail?.submission_id ||
                        selectedSubmission?.submission_id ||
                        selectedSubmissionDetail?.id ||
                        selectedSubmission?.id,
                      "Submission"
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <MetaField
                      label="Framework"
                      value={selectedSubmissionDetail?.framework || selectedSubmission?.framework}
                    />
                    <MetaField
                      label="Status"
                      value={
                        selectedSubmissionDetail?.status ||
                        selectedSubmissionDetail?.ack_status ||
                        selectedSubmission?.status ||
                        selectedSubmission?.ack_status
                      }
                    />
                    <MetaField
                      label="Linked Pack"
                      value={
                        selectedSubmissionDetail?.linked_pack_id ||
                        selectedSubmission?.linked_pack_id ||
                        selectedSubmissionDetail?.pack_id ||
                        selectedSubmission?.pack_id
                      }
                    />
                    <MetaField
                      label="Linked Report"
                      value={
                        selectedSubmissionDetail?.linked_report?.pack_id ||
                        selectedSubmission?.linked_report?.pack_id ||
                        selectedSubmissionDetail?.report_id ||
                        selectedSubmission?.report_id
                      }
                    />
                    <MetaField
                      label="Linked Archive"
                      value={selectedSubmissionDetail?.archive_id || selectedSubmission?.archive_id}
                    />
                    <MetaField
                      label="Created"
                      value={formatTimestamp(
                        selectedSubmissionDetail?.created_at || selectedSubmission?.created_at
                      )}
                    />
                  </div>

                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    {getLinkedReportId(selectedSubmissionDetail || selectedSubmission) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedReport(selectedSubmissionDetail || selectedSubmission)}
                      >
                        Open Linked Report
                      </button>
                    ) : null}

                    {getLinkedArchiveId(selectedSubmissionDetail || selectedSubmission) ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => openLinkedArchive(selectedSubmissionDetail || selectedSubmission)}
                      >
                        Open Linked Archive
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="muted">Select a submission to inspect its related proof chain.</div>
              )}
            </section>
          </aside>
        </div>

        <ContextualExplainDrawer
          open={evidenceExplainOpen}
          record={archiveDetail || selectedArchive}
          recordLabel={selectedArchiveExplainLabel}
          question={evidenceExplainQuestion}
          card={evidenceExplainCard}
          metadata={evidenceExplainCard?.metadata || null}
          loading={evidenceExplainLoading}
          error={evidenceExplainError}
          themeMode={resolvedThemeMode}
          prompts={EVIDENCE_EXPLAIN_PROMPTS}
          contextDescription="Grounded reasoning for this selected archive proof record. Deterministic archive integrity, chain verification, timestamp validation, and explicit audit-trail relationships remain authoritative."
          onAsk={askEvidenceExplain}
          onClose={closeEvidenceExplain}
        />
      </div>
    </div>
  );
}
